import { create } from 'zustand';
import { TaskInfo, AppNode, NodeStatus } from '../types';
import { config } from '../config';
import { generateApi, GenerateResult } from '../services/generateApi';
import { ApiError } from '../services/core/ApiError';
import { findStrategy, GenerationContext } from '../services/generationStrategies';
import { eventBus, CanvasEvents } from '../utils/eventBus';
import { useNodeStore } from './nodeStore';
import { useAgentStore } from './agentStore';
import { updateMessageForTaskStatus } from '../components/agent/task/messageStatusUtils';
import { isBatchTask } from '../components/agent/utils/batchTaskTracker';

interface TaskState {
  pendingTasks: Map<string, TaskInfo>;
  addTask: (taskId: string, nodeId: string) => void;
  addTaskWithRequest: (taskId: string, nodeId: string, nodeType: string, request: Record<string, any>) => void;
  updateTaskStatus: (taskId: string, status: TaskInfo['status']) => void;
  updateTasksBatch: (updates: Array<{ taskId: string; status: TaskInfo['status'] }>) => void;
  removeTask: (taskId: string) => void;
  getTaskByNodeId: (nodeId: string) => TaskInfo | undefined;
  loadFromLocalStorage: () => void;
  saveToLocalStorage: () => void;
  checkTaskStatus: (taskId: string) => Promise<TaskInfo['status']>;
  resumePendingTasks: (
    updateNode: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string, status?: NodeStatus) => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string, prompt?: string) => void,
    currentProjectId: string | null,
    onTaskCompleted?: (task: TaskInfo, updates: Partial<AppNode['data']>) => void,
  ) => void;
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;
}

const STORAGE_KEY = 'bong_ai_pending_tasks';
const _pollingTaskIds = new Set<string>();
// 🔴 真机⑩：localStorage 配额满告警去重（与 unifiedStorage.writeFailedKeys 同语义）
let _persistWarned = false;

// FE-P2-8: 5 个模块级 WebSocket 变量收敛为单一 _wsState 对象（最小化封装）
// 原 let 变量散落在模块作用域，封装后便于统一管理与未来抽离为 WebSocketManager 类。
const WS_MAX_RECONNECT_ATTEMPTS = 10;
const WS_BASE_RECONNECT_DELAY = 2000;
const _wsState: {
  connection: WebSocket | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconnectAttempts: number;
  manuallyClosed: boolean;
} = {
  connection: null,
  reconnectTimer: null,
  reconnectAttempts: 0,
  manuallyClosed: false,
};

export const useTaskStore = create<TaskState>((set, get) => ({
  pendingTasks: new Map(),

  addTask: (taskId, nodeId) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      newMap.set(taskId, {
        taskId,
        nodeId,
        status: 'RUNNING',
        startTime: Date.now(),
      });
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  addTaskWithRequest: (taskId, nodeId, nodeType, request) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      newMap.set(taskId, {
        taskId,
        nodeId,
        status: 'RUNNING',
        startTime: Date.now(),
        nodeType,
        request,
      });
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  updateTaskStatus: (taskId, status) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      const task = newMap.get(taskId);
      if (task) {
        newMap.set(taskId, { ...task, status });
      }
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  updateTasksBatch: (updates) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      let changed = false;
      for (const { taskId, status } of updates) {
        const task = newMap.get(taskId);
        if (task && task.status !== status) {
          newMap.set(taskId, { ...task, status });
          changed = true;
        }
      }
      if (!changed) return state;
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  removeTask: (taskId) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      newMap.delete(taskId);
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  getTaskByNodeId: (nodeId) => {
    const state = get();
    for (const task of state.pendingTasks.values()) {
      if (task.nodeId === nodeId) {
        return task;
      }
    }
    return undefined;
  },

  loadFromLocalStorage: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const tasks = JSON.parse(stored) as TaskInfo[];
        const newMap = new Map<string, TaskInfo>();
        tasks.forEach((task) => {
          newMap.set(task.taskId, task);
        });
        set({ pendingTasks: newMap });
      }
    } catch (e) {
      console.error('Failed to load tasks from localStorage:', e);
    }
  },

  saveToLocalStorage: () => {
    const tasks = Array.from(get().pendingTasks.values());
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
      _persistWarned = false;
      return;
    } catch { /* 落到配额自愈 */ }
    // 🔴 真机⑩：localStorage 配额满（bongai_* 备份互踢，见 unifiedStorage
    // 跨 key 淘汰日志）时原裸写直接失败，且旧值残留会在下次刷新时把陈旧
    // 任务列表复活（resumePendingTasks 轮询早已不存在的死任务）。三级自愈：
    // 1) 移除本 key 旧值重试（覆盖写场景，与 unifiedStorage 第一级自愈同款）
    // 2) 仍失败只保留 RUNNING 任务重试（终态任务对恢复无意义）
    // 3) 最终失败移除本 key 防陈旧复活，仅告警一次（内存态不受影响，
    //    画布结果回填链路 WS/DB 不经 localStorage）
    let saved = false;
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
      saved = true;
    } catch { /* 继续降级 */ }
    if (!saved) {
      try {
        const runningOnly = tasks.filter((t) => t.status === 'RUNNING');
        localStorage.setItem(STORAGE_KEY, JSON.stringify(runningOnly));
        saved = true;
      } catch { /* 继续降级 */ }
    }
    if (!saved) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* 忽略 */ }
      if (!_persistWarned) {
        _persistWarned = true;
        console.warn(
          '[TaskStore] pending tasks 持久化失败（localStorage 配额满，内存态有效，已清除陈旧持久化数据）',
        );
      }
    }
  },

  checkTaskStatus: async (taskId) => {
    try {
      const data = await generateApi.getTaskStatus(taskId);
      const statusMap: Record<string, TaskInfo['status']> = {
        pending: 'RUNNING',
        running: 'RUNNING',
        success: 'SUCCESS',
        failed: 'FAILED',
      };
      const status = statusMap[data.status] || 'FAILED';
      get().updateTaskStatus(taskId, status);
      return status;
    } catch (e) {
      console.error('Failed to check task status:', e);
      return 'FAILED';
    }
  },

  resumePendingTasks: (updateNode, handleAssetGenerated, currentProjectId, onTaskCompleted) => {
    const { pendingTasks } = get();
    const runningTasks: TaskInfo[] = [];
    pendingTasks.forEach((task) => {
      if (task.status === 'RUNNING') {
        runningTasks.push(task);
      }
    });

    if (runningTasks.length === 0) return;

    // 🔴 批量任务修复：错开轮询启动时间，避免多任务同时 HTTP 轮询压垮后端。
    // image2psd 批量提交 5+ 个任务时，同时发起 5 个 pollTaskUntilComplete 长轮询，
    // 后端正在处理 img2img 生成，并发 HTTP 请求导致 ReadError。
    // 每个任务间隔 1.5s 启动，给后端足够的连接处理余量。
    const STAGGER_DELAY_MS = 1500;
    // v1.4.33：重试条件从纯网络错误扩展为"网络 + 服务端 5xx/429/408"瞬态错误（见 pollWithRetry）
    const MAX_NETWORK_RETRIES = 3;

    runningTasks.forEach((task, taskIndex) => {
      if (_pollingTaskIds.has(task.taskId)) return;

      // 🔴 批量任务跳过 HTTP 轮询：由 WebSocket + batchTaskTracker 跟踪完成状态。
      // image2psd 批量提交 5+ 个任务时，同时发起多个 HTTP 长轮询会压垮后端连接（ReadError），
      // 而 WebSocket task_update 事件已能可靠通知任务完成 → taskResultSync → markTaskCompleted。
      if (isBatchTask(task.taskId)) {
        console.log(`[TaskResume] Skipping batch task ${task.taskId} (tracked by WebSocket + batchTaskTracker)`);
        return;
      }

      _pollingTaskIds.add(task.taskId);

      const staggerDelay = taskIndex * STAGGER_DELAY_MS;

      setTimeout(() => {
        // 🔴 网络错误重试：ReadError 等网络异常时重试，而非直接标记 FAILED。
        // 批量场景下后端并发压力可能导致偶发连接失败，重试可恢复。
        const pollWithRetry = async (retryCount: number): Promise<void> => {
          try {
            console.log(`[TaskResume] Resuming task ${task.taskId} for node ${task.nodeId} (attempt ${retryCount + 1}/${MAX_NETWORK_RETRIES + 1})`);

            const existingNode = useNodeStore?.getState?.()?.nodeMap?.get(task.nodeId);
            if (!existingNode) {
              // 🔴 真机⑬：跨项目/跨页面的陈旧任务（页面在完成前关闭错过 WS 终态，
              // pendingTasks 全局共享不分项目）恢复时 node 必然 miss——先查后端
              // 终态：已完成的任务静默清理（正常运维）；仅仍在运行却失去节点的
              // 任务才 warn（真异常：结果无处回填）
              const finalStatus = await get().checkTaskStatus(task.taskId).catch(() => 'FAILED' as const);
              if (finalStatus === 'SUCCESS' || finalStatus === 'FAILED') {
                console.info(
                  `[TaskResume] Node ${task.nodeId} 已不存在且任务已终态(${finalStatus})，静默清理陈旧任务 ${task.taskId}`,
                );
              } else {
                console.warn(
                  `[TaskResume] Node ${task.nodeId} no longer exists while task ${task.taskId} 仍在运行，移除任务`,
                );
              }
              get().removeTask(task.taskId);
              _pollingTaskIds.delete(task.taskId);
              return;
            }

            updateNode(task.nodeId, { error: undefined, progress: '恢复任务中...', _needsTaskRecovery: false }, undefined, undefined, NodeStatus.WORKING);

            const statusResult = await generateApi.pollTaskUntilComplete(task.taskId, (status) => {
              if (status.status === 'running') {
                updateNode(task.nodeId, { progress: '生成中...' }, undefined, undefined, NodeStatus.WORKING);
              }
            });

            if (statusResult.status === 'failed') {
              throw new Error(statusResult.error || '任务失败');
            }

            const resultData = statusResult.result;
            if (!resultData) {
              throw new Error('未收到生成结果');
            }

            const nodeType = task.nodeType || '';
            const strategy = findStrategy(nodeType as any);
            const request = task.request || {};
            const prompt = request.prompt || '';

            if (strategy) {
              const mockNode: AppNode = {
                id: task.nodeId,
                type: nodeType as any,
                x: 0, y: 0,
                title: '',
                status: NodeStatus.WORKING,
                data: {
                  model: request.modelId,
                  prompt: request.prompt,
                  aspectRatio: request.params?.aspectRatio,
                  resolution: request.params?.resolution,
                  imageCount: request.params?.imageCount,
                  duration: request.params?.duration,
                  videoCount: request.params?.videoCount,
                  generationMode: request.params?.generationMode,
                  generateAudio: request.params?.generateAudio,
                  panoramaQuality: request.params?.quality,
                  // 智能剪辑导出（§4.9）：透传导出目标，videoComposeStrategy.processResult
                  // 依赖 node.data.target 分发 local/canvas/davinci，缺失会误降级为 local
                  ...(request.target ? { target: request.target } : {}),
                },
                inputs: [],
              };

              const rawResult: GenerateResult = {
                taskId: statusResult.taskId,
                status: statusResult.status,
                results: resultData.results,
                error: resultData.error,
                videoMetadata: resultData.videoMetadata,
                analysis: resultData.analysis,
                // v1.4.51 恢复链对齐主动生成链：透传 LLM 结构化产物 data（shots/entities/style）。
                // 事故背景：脚本任务后端已 success（24 个分镜完整落库），页面刷新后 TaskResume
                // 拿到 200 却因漏传 data，script.processResult 拿到空 payload 报
                // 「未收到分镜脚本数据」，已成功成果被误判为恢复失败
                data: resultData.data,
              };

              const ctx: GenerationContext = {
                projectId: currentProjectId,
                onProgress: () => {},
                onAssetGenerated: handleAssetGenerated,
                onTaskCreated: () => {},
              };

              const updates = await strategy.processResult(mockNode, rawResult, ctx, prompt);
              updateNode(task.nodeId, updates, undefined, undefined, NodeStatus.SUCCESS);

              if (updates.image) handleAssetGenerated('image', updates.image, '', request.prompt);
              if (updates.videoUri) handleAssetGenerated('video', updates.videoUri, '', request.prompt);
              if (updates.audioUri) handleAssetGenerated('audio', updates.audioUri, '', request.prompt);

              if (onTaskCompleted) {
                onTaskCompleted(task, updates);
              }
            } else {
              updateNode(task.nodeId, {}, undefined, undefined, NodeStatus.SUCCESS);
            }

            get().updateTaskStatus(task.taskId, 'SUCCESS');
            get().removeTask(task.taskId);
            _pollingTaskIds.delete(task.taskId);
            console.log(`[TaskResume] Task ${task.taskId} resumed successfully`);
          } catch (e: any) {
            // v1.4.33 瞬态故障容忍扩容：服务端 5xx（internal_error/502/503，如后端重启窗口）
            // 与网络异常同样可重试——任务终态以后端 DB 记录为准，后端恢复后可原样捞回结果，
            // 避免"任务实际已 success 却被标记生成失败 internal_error"。pollTaskUntilComplete
            // 内部已容忍连续 5 次轮询失败，冒到这里的 5xx 属于持续故障，再给 3 轮兜底。
            const isRetryableError =
              (e instanceof ApiError && (e.status >= 500 || e.status === 429 || e.status === 408)) ||
              e.message?.includes('ReadError') ||
              e.message?.includes('网络异常') ||
              e.message?.includes('FetchError') ||
              e.name === 'AbortError';

            if (isRetryableError && retryCount < MAX_NETWORK_RETRIES) {
              // 可重试错误（网络/服务端瞬态）：等待后重试，不标记 FAILED
              const retryDelay = (retryCount + 1) * 3000; // 3s, 6s, 9s
              console.warn(`[TaskResume] Task ${task.taskId} retryable error (attempt ${retryCount + 1}), retrying in ${retryDelay}ms: ${e.message}`);
              setTimeout(() => pollWithRetry(retryCount + 1), retryDelay);
              return;
            }

            // 非可重试错误或重试耗尽：标记 FAILED
            console.error(`[TaskResume] Failed to resume task ${task.taskId}:`, e);
            updateNode(task.nodeId, { error: e.message || '恢复任务失败', progress: undefined }, undefined, undefined, NodeStatus.ERROR);
            get().updateTaskStatus(task.taskId, 'FAILED');
            get().removeTask(task.taskId);
            _pollingTaskIds.delete(task.taskId);
          }
        };

        pollWithRetry(0);
      }, staggerDelay);
    });
  },

  connectWebSocket: () => {
    _wsState.manuallyClosed = false;
    if (_wsState.connection && (_wsState.connection.readyState === WebSocket.OPEN || _wsState.connection.readyState === WebSocket.CONNECTING)) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = config.apiBase.replace(/^https?:/, protocol);
    const url = `${wsUrl}/ws/tasks?client_id=bong-ai-front`;

    try {
      _wsState.connection = new WebSocket(url);

      _wsState.connection.onopen = () => {
        console.log('[TaskWS] Connected');
        _wsState.reconnectAttempts = 0;
        // 🔴 根因修复：WS（重）连成功时机对本地 RUNNING 残留做校验闭环。
        // 后端重启会杀死 in-flight 任务且不补发终态事件，若前端页面未刷新，
        // 面板已挂载的恢复消息无从触发清理——由监听方（useTaskEventListeners）
        // 收到本事件后向后端校验并走统一收尾，替代永远等不到的 WS 终态事件。
        eventBus.emit(CanvasEvents.TASK_WS_CONNECTED);
      };

      _wsState.connection.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'task_update' && msg.task_id && msg.data) {
            const taskStatus = msg.data.status;
            const taskResult = msg.data.result;
            const taskError = msg.data.error;

            // 中间态处理（queued/running）：直接更新面板消息 mediaData.status（P1-1 合并：迁移自 useCollaborationWebSocket）
            // P2-2 + P2-4: 使用统一的 updateMessageForTaskStatus 工具函数
            if (taskStatus === 'queued' || taskStatus === 'running') {
              const midStatus = taskStatus === 'queued' ? 'QUEUED' : 'RUNNING';
              useAgentStore.getState().setMessages((prev) =>
                updateMessageForTaskStatus(prev, msg.task_id, {
                  status: midStatus,
                  taskStatus: midStatus,
                }).messages
              );
              return;
            }

            // final 态处理（success/failed）
            const wsStatusMap: Record<string, TaskInfo['status']> = {
              success: 'SUCCESS',
              failed: 'FAILED',
            };
            const status = wsStatusMap[taskStatus];
            if (!status) return;

            get().updateTaskStatus(msg.task_id, status);

            const task = get().pendingTasks.get(msg.task_id);
            if (task && task.nodeId && task.nodeId.startsWith('agent-node-')) {
              // 统一走 AGENT_TASK_COMPLETED 事件（含 FAILED，修复 P0 Bug）
              if (!_pollingTaskIds.has(msg.task_id)) {
                eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                  taskId: msg.task_id,
                  nodeId: task.nodeId,
                  status,
                  updates: taskResult || {},
                  ...(taskError ? { error: taskError } : {}),
                });
              }
            } else if (task && task.nodeId && status === 'SUCCESS' && !_pollingTaskIds.has(msg.task_id)) {
              eventBus.emit(CanvasEvents.TASK_COMPLETED, {
                taskId: msg.task_id,
                nodeId: task.nodeId,
                nodeType: task.nodeType || '',
                request: task.request || {},
                result: taskResult || {},
              });
            } else if (!task) {
              // taskStore 中无记录：直接更新面板消息（P1-1 合并：迁移自 useCollaborationWebSocket）
              // P2-2 + P2-4: 使用统一的 updateMessageForTaskStatus 工具函数
              useAgentStore.getState().setMessages((prev) =>
                updateMessageForTaskStatus(prev, msg.task_id, {
                  status,
                  taskStatus: status === 'SUCCESS' ? 'COMPLETED' : 'FAILED',
                  results: taskResult?.results,
                  videoMetadata: taskResult?.videoMetadata,
                  error: taskError,
                  text: status === 'FAILED' ? `生成失败：${taskError || '未知错误'}` : '',
                }).messages
              );
            }
            get().removeTask(msg.task_id);
            _pollingTaskIds.delete(msg.task_id);
          }
        } catch {}
      };

      _wsState.connection.onclose = () => {
        console.log('[TaskWS] Disconnected');
        _wsState.connection = null;
        if (_wsState.manuallyClosed) return;
        if (_wsState.reconnectAttempts >= WS_MAX_RECONNECT_ATTEMPTS) {
          console.warn('[TaskWS] Max reconnect attempts reached, giving up');
          return;
        }
        const delay = WS_BASE_RECONNECT_DELAY * Math.pow(1.5, _wsState.reconnectAttempts);
        _wsState.reconnectAttempts++;
        console.log(`[TaskWS] Reconnecting in ${Math.round(delay)}ms (attempt ${_wsState.reconnectAttempts}/${WS_MAX_RECONNECT_ATTEMPTS})...`);
        _wsState.reconnectTimer = setTimeout(() => {
          get().connectWebSocket();
        }, delay);
      };

      _wsState.connection.onerror = () => {
        _wsState.connection?.close();
      };
    } catch {
      if (_wsState.manuallyClosed) return;
      if (_wsState.reconnectAttempts < WS_MAX_RECONNECT_ATTEMPTS) {
        const delay = WS_BASE_RECONNECT_DELAY * Math.pow(1.5, _wsState.reconnectAttempts);
        _wsState.reconnectAttempts++;
        _wsState.reconnectTimer = setTimeout(() => {
          get().connectWebSocket();
        }, delay);
      }
    }
  },

  disconnectWebSocket: () => {
    _wsState.manuallyClosed = true;
    if (_wsState.reconnectTimer) {
      clearTimeout(_wsState.reconnectTimer);
      _wsState.reconnectTimer = null;
    }
    if (_wsState.connection) {
      _wsState.connection.onclose = null;
      _wsState.connection.close();
      _wsState.connection = null;
    }
    _wsState.reconnectAttempts = 0;
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
